import React, { useEffect, useMemo, useState } from 'react';
import applicationService from '../services/applicationService';
import corporateService from '../services/corporateService';

const currency = (value) => {
  const amount = Number(value || 0);
  if (Number.isNaN(amount)) return 'INR 0.00';
  return `INR ${amount.toFixed(2)}`;
};

const refundBadgeClass = (value) => {
  if (value === 'approved') return 'bg-[#d4edda] text-[#155724]';
  if (value === 'rejected') return 'bg-[#f8d7da] text-[#721c24]';
  return 'bg-[#fff3cd] text-[#856404]';
};

const OfferLetterSection = ({ application, candidate, userRole, onRefresh }) => {
  const isHrAdmin = userRole === 'HR' || userRole === 'ADMIN' || userRole === 'CHAIRMAN';
  const isCandidate = userRole === 'CANDIDATE';
  const showPaymentSection = !!application?.offerLetter?.fileUrl;
  const [loading, setLoading] = useState(false);
  const [signedFile, setSignedFile] = useState(null);
  const [offerLetterFile, setOfferLetterFile] = useState(null);
  const [reviewReason, setReviewReason] = useState('');
  const [paymentAmountInput, setPaymentAmountInput] = useState('');
  const [paymentPayerType, setPaymentPayerType] = useState('candidate');
  const [paymentTransactionId, setPaymentTransactionId] = useState('');
  const [paymentCorporateName, setPaymentCorporateName] = useState('');
  const [corporates, setCorporates] = useState([]);
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [paymentReviewReason, setPaymentReviewReason] = useState('');
  const [paymentRefundReason, setPaymentRefundReason] = useState('');

  const initialForm = useMemo(() => {
    const offer = application?.offerLetter || {};
    const personal = candidate?.personalInformation || {};

    return {
      name: offer.name || personal.fullName || candidate?.fullName || '',
      passportNumber: offer.passportNumber || personal.passportNumber || '',
      companyName: offer.companyName || 'Jupiter International',
      basicSalary: offer.basicSalary ?? '',
      nightShiftAllowance: offer.nightShiftAllowance ?? '',
      workingHours: offer.workingHours || '8 hours/day'
    };
  }, [application?.offerLetter, candidate]);

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    setFormData(initialForm);
  }, [initialForm]);

  const offerLetter = application?.offerLetter || {};
  const signedReviewStatus = offerLetter?.adminReviewStatus;
  const offerPaymentStatus = offerLetter?.paymentStatus || 'pending';
  const offerPaymentRefundRequest = offerLetter?.paymentRefundRequest;

  useEffect(() => {
    setPaymentAmountInput(
      offerLetter?.paymentAmount === undefined || offerLetter?.paymentAmount === null
        ? ''
        : String(offerLetter.paymentAmount)
    );
  }, [offerLetter?.paymentAmount]);

  useEffect(() => {
    setPaymentPayerType(offerLetter?.paymentPayerType || 'candidate');
  }, [offerLetter?.paymentPayerType]);

  useEffect(() => {
    setPaymentCorporateName(offerLetter?.paymentCorporateName || '');
  }, [offerLetter?.paymentCorporateName]);

  useEffect(() => {
    // Fetch corporates list
    const fetchCorporates = async () => {
      try {
        const response = await corporateService.getAllCorporates();
        if (response.success) {
          setCorporates(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch corporates:', error);
      }
    };
    fetchCorporates();
  }, []);

  const effectivePaymentPayerType = offerLetter?.paymentPayerType || 'candidate';
  const canRequestOfferPaymentRefund =
    isCandidate &&
    effectivePaymentPayerType === 'candidate' &&
    offerPaymentStatus === 'verified' &&
    (!offerPaymentRefundRequest?.status || offerPaymentRefundRequest.status === 'rejected');

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const generateOfferLetter = async () => {
    const required = ['name', 'passportNumber', 'companyName', 'basicSalary', 'nightShiftAllowance', 'workingHours'];
    const missing = required.find((field) => String(formData[field] ?? '').trim() === '');
    if (missing) {
      alert('Please fill all offer letter fields before generating.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        basicSalary: Number(formData.basicSalary),
        nightShiftAllowance: Number(formData.nightShiftAllowance)
      };

      if (Number.isNaN(payload.basicSalary) || Number.isNaN(payload.nightShiftAllowance)) {
        alert('Salary and allowance must be valid numbers.');
        return;
      }

      const response = await applicationService.generateOfferLetter(application._id, payload);
      if (response.success) {
        alert('Offer letter generated successfully');
        onRefresh();
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to generate offer letter');
    } finally {
      setLoading(false);
    }
  };

  const uploadSignedOfferLetter = async () => {
    if (!signedFile) {
      alert('Please select signed offer letter file to upload.');
      return;
    }

    setLoading(true);
    try {
      const response = await applicationService.uploadSignedOfferLetter(application._id, signedFile);
      if (response.success) {
        alert('Signed offer letter uploaded successfully');
        setSignedFile(null);
        onRefresh();
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to upload signed offer letter');
    } finally {
      setLoading(false);
    }
  };

  const uploadOfferLetterByAdmin = async () => {
    if (!offerLetterFile) {
      alert('Please select offer letter file to upload.');
      return;
    }

    setLoading(true);
    try {
      const response = await applicationService.uploadOfferLetter(application._id, offerLetterFile);
      if (response.success) {
        alert('Offer letter uploaded successfully');
        setOfferLetterFile(null);
        onRefresh();
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to upload offer letter');
    } finally {
      setLoading(false);
    }
  };

  const reviewSignedOfferLetter = async (action) => {
    if (action === 'reject' && String(reviewReason || '').trim().length < 5) {
      alert('Please provide rejection reason (minimum 5 characters).');
      return;
    }

    setLoading(true);
    try {
      const response = await applicationService.reviewSignedOfferLetter(
        application._id,
        action,
        action === 'reject' ? reviewReason.trim() : undefined
      );

      if (response.success) {
        alert(action === 'accept' ? 'Signed offer accepted. Application moved to Payment stage.' : 'Signed offer rejected. Candidate can upload again.');
        setReviewReason('');
        onRefresh();
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to review signed offer letter');
    } finally {
      setLoading(false);
    }
  };

  const setupPaymentAmount = async () => {
    if (String(paymentAmountInput).trim() === '') {
      alert('Please enter payment amount.');
      return;
    }

    const amount = Number(paymentAmountInput);
    if (!Number.isFinite(amount) || amount < 0) {
      alert('Payment amount must be a non-negative number.');
      return;
    }

    setLoading(true);
    try {
      const response = await applicationService.setupOfferLetterPayment(application._id, amount, paymentPayerType);
      if (response.success) {
        alert('Payment settings saved.');
        onRefresh();
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save payment amount');
    } finally {
      setLoading(false);
    }
  };

  const uploadPaymentProof = async () => {
    const payerType = effectivePaymentPayerType;

    if (!String(paymentTransactionId).trim()) {
      alert('Please enter transaction ID.');
      return;
    }

    if (payerType === 'corporate' && !String(paymentCorporateName).trim()) {
      alert('Please enter corporate name.');
      return;
    }

    if (!paymentProofFile) {
      alert('Please upload payment proof file.');
      return;
    }

    setLoading(true);
    try {
      const response = await applicationService.uploadOfferLetterPayment(application._id, {
        transactionId: paymentTransactionId.trim(),
        corporateName: payerType === 'corporate' ? paymentCorporateName.trim() : undefined,
        file: paymentProofFile
      });
      if (response.success) {
        alert('Payment proof uploaded successfully.');
        setPaymentProofFile(null);
        setPaymentTransactionId('');
        onRefresh();
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to upload payment proof');
    } finally {
      setLoading(false);
    }
  };

  const reviewPaymentProof = async (action) => {
    if (action === 'reject' && String(paymentReviewReason).trim().length < 5) {
      alert('Please provide rejection reason (minimum 5 characters).');
      return;
    }

    setLoading(true);
    try {
      const response = await applicationService.reviewOfferLetterPayment(
        application._id,
        action,
        action === 'reject' ? paymentReviewReason.trim() : undefined
      );
      if (response.success) {
        alert(action === 'verify' ? 'Payment verified. Moved to Document Verification.' : 'Payment rejected. Candidate can upload again.');
        setPaymentReviewReason('');
        onRefresh();
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to review payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.05)] overflow-hidden">
      <div className="bg-[#1b355b] px-5 py-4 relative">
        <div className="h-1 w-full bg-[#d9382a] absolute left-0 bottom-0"></div>
        <h3 className="m-0 text-lg font-bold text-white tracking-wide">Offer Letter Generator</h3>
        <p className="m-0 mt-1 text-xs text-[#d4e0f2]">Auto-create the final offer letter with salary and shift details</p>
      </div>

      <div className="px-5 py-4 border-b border-[#d5dce8] bg-[linear-gradient(180deg,#f8fbff_0%,#ffffff_100%)]">
        <div className="text-[#1b355b] text-sm tracking-[2px] font-semibold">★ ★ ★ ★ ★ ★ ★ ★ ★ ★ ★ ★ ★ ★ ★ ★</div>
      </div>

      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Name</span>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            disabled={!isHrAdmin}
            className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-accent-green disabled:bg-gray-100"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Passport Number</span>
          <input
            type="text"
            value={formData.passportNumber}
            onChange={(e) => updateField('passportNumber', e.target.value)}
            disabled={!isHrAdmin}
            className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-accent-green disabled:bg-gray-100"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Company Name</span>
          <input
            type="text"
            value={formData.companyName}
            onChange={(e) => updateField('companyName', e.target.value)}
            disabled={!isHrAdmin}
            className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-accent-green disabled:bg-gray-100"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Working Hours</span>
          <input
            type="text"
            value={formData.workingHours}
            onChange={(e) => updateField('workingHours', e.target.value)}
            disabled={!isHrAdmin}
            className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-accent-green disabled:bg-gray-100"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Basic Salary</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={formData.basicSalary}
            onChange={(e) => updateField('basicSalary', e.target.value)}
            disabled={!isHrAdmin}
            className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-accent-green disabled:bg-gray-100"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Night Shift Allowance</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={formData.nightShiftAllowance}
            onChange={(e) => updateField('nightShiftAllowance', e.target.value)}
            disabled={!isHrAdmin}
            className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-accent-green disabled:bg-gray-100"
          />
        </label>
      </div>

      <div className="px-5 pb-5 flex flex-wrap gap-3 items-center">
        {isHrAdmin && (
          <>
            <button
              onClick={generateOfferLetter}
              disabled={loading}
              className="py-1.5 px-3 rounded-md font-semibold text-[13px] leading-8 h-fit min-h-0 cursor-pointer transition-all duration-200 border-none whitespace-nowrap inline-flex items-center justify-center align-middle box-border bg-primary text-white hover:bg-primary-hover disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Generate Offer Letter'}
            </button>

            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setOfferLetterFile(e.target.files?.[0] || null)}
              disabled={loading}
              className="p-2 border border-gray-300 rounded-md text-sm"
            />

            <button
              onClick={uploadOfferLetterByAdmin}
              disabled={loading || !offerLetterFile}
              className="py-1.5 px-3 rounded-md font-semibold text-[13px] leading-8 h-fit min-h-0 cursor-pointer transition-all duration-200 border-none whitespace-nowrap inline-flex items-center justify-center align-middle box-border bg-[#1b355b] text-white hover:bg-[#142844] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Upload Offer Letter'}
            </button>
          </>
        )}

        {offerLetter.fileUrl ? (
          <a
            href={offerLetter.fileUrl}
            target="_blank"
            rel="noreferrer"
            className="py-1.5 px-3 rounded-md font-semibold text-[13px] leading-8 h-fit min-h-0 cursor-pointer transition-all duration-200 border border-[#1b355b] whitespace-nowrap inline-flex items-center justify-center align-middle box-border text-[#1b355b] hover:bg-[#eef3fb]"
          >
            View Offer Letter (PDF)
          </a>
        ) : (
          <span className="text-sm text-text-secondary">No offer letter generated yet.</span>
        )}
      </div>

      {offerLetter.fileUrl && application?.currentStage === 'OFFER_LETTER' && isCandidate && (
        <div className="px-5 pb-5 border-t border-[#d5dce8] pt-4">
          <h4 className="m-0 mb-2 text-sm font-semibold text-primary">Candidate Action</h4>
          <p className="m-0 mb-3 text-xs text-text-secondary">Upload your signed offer letter. Admin will review and approve it before moving to Document Verification.</p>

          <div className="flex flex-wrap gap-3 items-center">
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => setSignedFile(e.target.files?.[0] || null)}
              disabled={loading || signedReviewStatus === 'accepted'}
              className="p-2 border border-gray-300 rounded-md text-sm"
            />
            <button
              onClick={uploadSignedOfferLetter}
              disabled={loading || !signedFile || signedReviewStatus === 'accepted'}
              className="py-1.5 px-3 rounded-md font-semibold text-[13px] border-none bg-primary text-white hover:bg-primary-hover disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Uploading...' : 'Upload Signed Offer Letter'}
            </button>

            {offerLetter.signedFileUrl && (
              <a
                href={offerLetter.signedFileUrl}
                target="_blank"
                rel="noreferrer"
                className="py-1.5 px-3 rounded-md font-semibold text-[13px] border border-[#1b355b] text-[#1b355b] hover:bg-[#eef3fb]"
              >
                View Uploaded Signed Offer
              </a>
            )}
          </div>

          {signedReviewStatus === 'pending' && (
            <div className="mt-2 text-sm text-amber-700">Signed offer letter uploaded. Waiting for admin approval.</div>
          )}
          {signedReviewStatus === 'accepted' && (
            <div className="mt-2 text-sm text-green-700">Signed offer letter approved by admin.</div>
          )}
          {signedReviewStatus === 'rejected' && (
            <div className="mt-2 text-sm text-red-700">Signed offer rejected: {offerLetter.adminReviewReason || 'Please upload again.'}</div>
          )}
        </div>
      )}

      {offerLetter.fileUrl && application?.currentStage === 'OFFER_LETTER' && isHrAdmin && offerLetter.signedFileUrl && (
        <div className="px-5 pb-5 border-t border-[#d5dce8] pt-4">
          <h4 className="m-0 mb-2 text-sm font-semibold text-primary">Admin Review of Signed Offer</h4>
          <div className="flex flex-wrap gap-3 items-center mb-3">
            <a
              href={offerLetter.signedFileUrl}
              target="_blank"
              rel="noreferrer"
              className="py-1.5 px-3 rounded-md font-semibold text-[13px] border border-[#1b355b] text-[#1b355b] hover:bg-[#eef3fb]"
            >
              View Candidate Signed Offer
            </a>

            {signedReviewStatus !== 'accepted' && (
              <>
                <button
                  onClick={() => reviewSignedOfferLetter('accept')}
                  disabled={loading}
                  className="py-1.5 px-3 rounded-md font-semibold text-[13px] border-none bg-green-600 text-white hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Accept & Move to Payment Stage
                </button>
                <button
                  onClick={() => reviewSignedOfferLetter('reject')}
                  disabled={loading}
                  className="py-1.5 px-3 rounded-md font-semibold text-[13px] border-none bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Reject Signed Offer
                </button>
              </>
            )}
          </div>

          {signedReviewStatus !== 'accepted' && (
            <input
              type="text"
              placeholder="Reason for rejection (required when rejecting)"
              value={reviewReason}
              onChange={(e) => setReviewReason(e.target.value)}
              className="w-full p-2.5 border border-gray-300 rounded-md text-sm"
            />
          )}

          {signedReviewStatus === 'accepted' && (
            <div className="text-sm text-green-700">Signed offer accepted by admin.</div>
          )}
          {signedReviewStatus === 'rejected' && (
            <div className="text-sm text-red-700">Last rejection reason: {offerLetter.adminReviewReason || 'Not provided'}</div>
          )}
        </div>
      )}

      {offerLetter.fileUrl && showPaymentSection && (
        <div className="px-5 pb-5 border-t border-[#d5dce8] pt-4">
          <h4 className="m-0 mb-2 text-sm font-semibold text-primary">Payment Section</h4>

          {isHrAdmin && (
            <div className="mb-4">
              <label className="text-xs font-semibold text-text-secondary uppercase tracking-wide block mb-1">Payment Configuration</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentAmountInput}
                  onChange={(e) => setPaymentAmountInput(e.target.value)}
                  className="p-2.5 border border-gray-300 rounded-md text-sm"
                  placeholder="Payment amount"
                />
                <select
                  value={paymentPayerType}
                  onChange={(e) => setPaymentPayerType(e.target.value)}
                  className="p-2.5 border border-gray-300 rounded-md text-sm"
                >
                  <option value="candidate">Candidate Pays</option>
                  <option value="corporate">Corporate Pays</option>
                </select>
                <button
                  onClick={setupPaymentAmount}
                  disabled={loading}
                  className="py-1.5 px-3 rounded-md font-semibold text-[13px] border-none bg-primary text-white hover:bg-primary-hover disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Save & Request Payment
                </button>
              </div>
            </div>
          )}

          <div className="text-sm mb-2">Amount: <span className="font-semibold">{offerLetter?.paymentAmount ?? 0}</span></div>
          <div className="text-sm mb-2">Payer Type: <span className="font-semibold uppercase">{effectivePaymentPayerType}</span></div>
          {offerLetter?.paymentCorporateName && (
            <div className="text-sm mb-2">Corporate Name: <span className="font-semibold">{offerLetter.paymentCorporateName}</span></div>
          )}
          <div className="text-sm mb-2">Payment Status: <span className="font-semibold uppercase">{offerPaymentStatus}</span></div>

          {isCandidate && effectivePaymentPayerType === 'candidate' && (
            <div className="mb-4">
              <p className="m-0 mb-2 text-xs text-text-secondary">Enter transaction ID and upload payment proof after bank payment.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Transaction ID"
                  value={paymentTransactionId}
                  onChange={(e) => setPaymentTransactionId(e.target.value)}
                  className="p-2.5 border border-gray-300 rounded-md text-sm"
                />
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setPaymentProofFile(e.target.files?.[0] || null)}
                  className="p-2.5 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <button
                onClick={uploadPaymentProof}
                disabled={loading}
                className="py-1.5 px-3 rounded-md font-semibold text-[13px] border-none bg-primary text-white hover:bg-primary-hover disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Upload Payment Proof
              </button>
            </div>
          )}

          {isCandidate && effectivePaymentPayerType === 'corporate' && (
            <div className="mb-4 text-sm text-text-secondary">
              Payment is configured as corporate-paid. HR/Admin will upload payment details.
            </div>
          )}

          {isHrAdmin && effectivePaymentPayerType === 'corporate' && offerPaymentStatus !== 'verified' && (
            <div className="mb-4">
              <p className="m-0 mb-2 text-xs text-text-secondary">Upload corporate payment details and proof.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                <select
                  value={paymentCorporateName}
                  onChange={(e) => setPaymentCorporateName(e.target.value)}
                  className="p-2.5 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Select Corporate</option>
                  {corporates.map((corporate) => (
                    <option key={corporate._id} value={corporate.name}>
                      {corporate.name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Transaction ID"
                  value={paymentTransactionId}
                  onChange={(e) => setPaymentTransactionId(e.target.value)}
                  className="p-2.5 border border-gray-300 rounded-md text-sm"
                />
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setPaymentProofFile(e.target.files?.[0] || null)}
                  className="p-2.5 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <button
                onClick={uploadPaymentProof}
                disabled={loading}
                className="py-1.5 px-3 rounded-md font-semibold text-[13px] border-none bg-primary text-white hover:bg-primary-hover disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Upload Corporate Payment Proof
              </button>
            </div>
          )}

          {offerLetter?.paymentTransactionId && (
            <div className="text-sm mb-1">Transaction ID: <span className="font-semibold">{offerLetter.paymentTransactionId}</span></div>
          )}
          {offerLetter?.paymentProofUrl && (
            <div className="mb-2">
              <a
                href={offerLetter.paymentProofUrl}
                target="_blank"
                rel="noreferrer"
                className="py-1 px-2 rounded border border-[#1b355b] text-[#1b355b] text-xs"
              >
                View Payment Proof
              </a>
            </div>
          )}

          {isHrAdmin && offerLetter?.paymentProofUrl && offerPaymentStatus !== 'verified' && (
            <div>
              <input
                type="text"
                placeholder="Reason for rejection (required when rejecting)"
                value={paymentReviewReason}
                onChange={(e) => setPaymentReviewReason(e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-md text-sm mb-2"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => reviewPaymentProof('verify')}
                  disabled={loading}
                  className="py-1.5 px-3 rounded-md font-semibold text-[13px] border-none bg-green-600 text-white hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Verify Payment & Move to Documents
                </button>
                <button
                  onClick={() => reviewPaymentProof('reject')}
                  disabled={loading}
                  className="py-1.5 px-3 rounded-md font-semibold text-[13px] border-none bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Reject Payment
                </button>
              </div>
            </div>
          )}

          {offerLetter?.paymentRejectedReason && (
            <div className="mt-2 text-sm text-red-700">Payment rejection reason: {offerLetter.paymentRejectedReason}</div>
          )}

          {offerPaymentRefundRequest?.status && (
            <div className="mt-2 text-sm">
              Refund request status:{' '}
              <span className={`px-2 py-1 rounded text-xs ${refundBadgeClass(offerPaymentRefundRequest.status)}`}>
                {offerPaymentRefundRequest.status}
              </span>
            </div>
          )}

          {offerPaymentRefundRequest?.reason && (
            <div className="mt-2 text-sm">Refund reason: <span className="font-semibold">{offerPaymentRefundRequest.reason}</span></div>
          )}

          {offerPaymentRefundRequest?.reviewDecisionReason && (
            <div className="mt-2 text-sm text-red-700">Chairman note: {offerPaymentRefundRequest.reviewDecisionReason}</div>
          )}

          {canRequestOfferPaymentRefund && (
            <div className="mt-3 border border-gray-200 rounded p-3 bg-[#fffaf0]">
              <div className="text-sm font-semibold mb-2">Request Payment Refund</div>
              <input
                type="text"
                placeholder="Reason for refund (minimum 5 characters)"
                value={paymentRefundReason}
                onChange={(e) => setPaymentRefundReason(e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-md text-sm mb-2"
              />
              <button
                onClick={async () => {
                  if (String(paymentRefundReason).trim().length < 5) {
                    alert('Please enter a valid refund reason (minimum 5 characters).');
                    return;
                  }

                  setLoading(true);
                  try {
                    const response = await applicationService.requestOfferLetterPaymentRefund(
                      application._id,
                      paymentRefundReason.trim()
                    );
                    if (response.success) {
                      alert('Refund request submitted successfully.');
                      setPaymentRefundReason('');
                      onRefresh();
                    }
                  } catch (error) {
                    alert(error.response?.data?.error || 'Failed to submit refund request');
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                className="py-1.5 px-3 rounded-md font-semibold text-[13px] border-none bg-[#d9534f] text-white hover:bg-[#c9302c] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Submit Refund Request
              </button>
            </div>
          )}
        </div>
      )}

      {offerLetter.generatedAt && (
        <div className="px-5 pb-5 text-xs text-text-secondary">
          Last generated: {new Date(offerLetter.generatedAt).toLocaleString()} | Salary: {currency(offerLetter.basicSalary)} | Night Shift Allowance: {currency(offerLetter.nightShiftAllowance)}
        </div>
      )}
    </div>
  );
};

export default OfferLetterSection;
